/* eslint-disable no-unused-vars */
import { Component, ComponentArgs } from "@kluntje/core";
import { v4 as uuidv4 } from "uuid";
import { ERROR, DISABLED, ACTIVE } from "Constants/cssClasses";
import { addClass, removeClass, toggleClass } from "@kluntje/js-utils/lib/dom-helpers";
import URLSearchParamsService from "Services/URLSearchParamsService";

import { FormFieldSubmitValue } from "./wec-e-form-field.types";

export class FormField<T = string> extends Component {
  private uuid = uuidv4();
  private _hasExternalError = false;

  constructor({ ui = {}, events = [], initialStates = {}, reactions = {} }: ComponentArgs = {}) {
    super({
      ui: {
        input: ".wec-js-form-field__input :-one",
      },
      events: [
        {
          event: "focusin",
          handler: "handleFocusIn",
          target: "input",
        },
        {
          event: "focusout",
          handler: "handleFocusOut",
          target: "input",
        },
      ],
      asyncRendering: true,
      initialStates: {
        showError: false,
        inValidationMode: false,
      },
      reactions: {
        showError: ["onErrorChange"],
        inValidationMode: ["onValidationModeChange"],
      },
    });

    Object.assign(this.ui, ui);
    Object.assign(this.initialStates, initialStates);
    Object.assign(this.reactions, reactions);

    this.mergeEvents(events);
  }

  static get observedAttributes() {
    return ["disabled"];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;
    this.renderComponent();

    if (name === "disabled") toggleClass(this, DISABLED, this.isDisabled);
  }

  /**
   * Returns default error message
   * @returns {string}
   */
  get DEFAULT_ERROR_MESSAGE(): string {
    return "Bitte füllen Sie dieses Feld aus.";
  }

  /**
   * Returns default field type
   * @returns {string}
   */
  get DEFAULT_FIELD_TYPE(): string {
    return "text";
  }

  /**
   * Returns default input placeholder
   * @returns {string}
   */
  get DEFAULT_PLACEHOLDER(): string {
    return "";
  }

  get id() {
    return this.uuid;
  }

  /**
   * Returns whether form-field is required
   * @returns {boolean}
   */
  get isRequired(): boolean {
    return this.getAttribute("required") !== null;
  }

  /**
   * Returns autocomplete type of input field
   * @returns {string}
   */
  get autocompleteType(): string | undefined {
    return this.getAttribute("autocomplete") || undefined;
  }

  /**
   * Returns name of input field
   * @returns {string}
   */
  get name(): string {
    return this.getAttribute("name") || "";
  }

  /**
   * Returns placeholder of input field
   * @returns {string}
   */
  get placeholder(): string {
    return this.getAttribute("placeholder") || this.DEFAULT_PLACEHOLDER;
  }

  /**
   * Returns error-message
   * @returns {string}
   */
  get errorMessage(): string {
    return this.getAttribute("error-message") || this.DEFAULT_ERROR_MESSAGE;
  }

  get description(): string {
    return this.getAttribute("description") || "";
  }

  /**
   * Returns type of input field
   * @returns {string}
   */
  get type(): string {
    return this.getAttribute("type") || this.DEFAULT_FIELD_TYPE;
  }

  /**
   * Returns label of input field
   * @returns {string}
   */
  get label(): string {
    return this.getAttribute("label") || "";
  }

  get prefillName(): string | null {
    return this.getAttribute("prefill-name");
  }

  get isSmall(): boolean {
    return this.hasAttribute("is-small") && this.getAttribute("is-small") !== "false";
  }
  /**
   * Returns whether form-field is disabled
   * @returns {string}
   */
  get isDisabled(): boolean {
    return this.hasAttribute("disabled") && this.getAttribute("disabled") !== "false";
  }

  /**
   * Set disabled attribute on Form-field
   * @param {boolean} newValue
   */
  set isDisabled(newValue: boolean) {
    if (newValue === true) {
      this.setAttribute("disabled", "");
    } else {
      this.removeAttribute("disabled");
    }
  }

  get isReadonly(): boolean {
    return this.hasAttribute("readonly") && this.getAttribute("readonly") !== "false";
  }

  /**
   * Returns whether input is currently valid
   * @returns {boolean}
   */
  get isValid(): boolean {
    return !this.hasExternalError && this.ui.input.checkValidity();
  }

  get submitValueType(): string {
    return "string";
  }

  /**
   * Returns the submit-value of the form-field
   * @returns {string}
   */
  get submitValue(): T | undefined {
    if (this.ui.input.value === undefined) return undefined;
    if (this.ui.input.value === "") return undefined;

    return this.ui.input.value;
  }

  /**
   * Returns the submit-data object of this form-field
   * @returns {object | false}
   */
  get submitData(): FormFieldSubmitValue<T> | null {
    if (this.name === "" || this.submitValue === undefined) return null;

    return {
      name: this.name,
      type: this.submitValueType,
      value: this.submitValue,
    };
  }

  get hasExternalError() {
    return this._hasExternalError;
  }

  set hasExternalError(newValue: boolean) {
    if (this._hasExternalError === newValue) return;

    this._hasExternalError = newValue;

    if (newValue === true) {
      this.setState({ showError: true });
    }
  }

  get prefillValue() {
    const prefillName = this.prefillName || this.name;
    if (prefillName === "") return null;
    return URLSearchParamsService.get(prefillName);
  }

  afterComponentRender() {
    this.prefillInput();
  }

  prefillInput() {
    const prefillValue = this.prefillValue;
    if (prefillValue === null) return;
    this.ui.input.value = prefillValue;
    this.renderComponent();
  }

  /**
   * Validates this form-field by updating show error state
   */
  validate(): void {
    this.setState({ showError: !this.isValid, inValidationMode: true });
  }

  /**
   * Handles showError-state changes by re-rendering the component
   * @param { showError } state
   */
  onErrorChange({ showError }: any): void {
    toggleClass(this, ERROR, showError);
    this.renderComponent();
  }

  /**
   * Handles showError-state changes by re-rendering the component
   * @param { inValidationMode } state
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onValidationModeChange({ inValidationMode }: any) {
    // can be overridden by extender later
  }

  handleFocusIn() {
    addClass(this, ACTIVE);
  }

  handleFocusOut() {
    removeClass(this, ACTIVE);
  }
}
